import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Mail, User, Zap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { EyeCaptainLogo } from "@/components/brand/EyeCaptainLogo";
import { subscribeNewsletter } from "@/lib/leads";

interface NewsletterPopupProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORY_COPY = {
  wait: {
    headline: { el: "\u039c\u03b9\u03c3\u03cc \u03bb\u03b5\u03c0\u03c4\u03cc!", en: "Hold up!" },
    subtitle: { el: "\u039c\u03c5\u03c3\u03c4\u03b9\u03ba\u03ac \u03c0\u03bf\u03c5 \u03bc\u03b5\u03c4\u03b1\u03c4\u03c1\u03ad\u03c0\u03bf\u03c5\u03bd bounce \u03c3\u03b5 conversion.", en: "Secrets that turn bounce into conversion." },
    cta: { el: "\u0394\u03b5\u03af\u03be\u03b5 \u03bc\u03bf\u03c5 \u03c4\u03b1 \u03bc\u03c5\u03c3\u03c4\u03b9\u03ba\u03ac", en: "Show me the secrets" },
  },
  dontLeave: {
    headline: { el: "\u039c\u03b7 \u03c6\u03b5\u03cd\u03b3\u03b5\u03b9\u03c2!", en: "Don't leave!" },
    subtitle: { el: "\u0394\u03c9\u03c1\u03b5\u03ac\u03bd CRO insights \u03c0\u03bf\u03c5 \u03b1\u03c5\u03be\u03ac\u03bd\u03bf\u03c5\u03bd \u03c0\u03c9\u03bb\u03ae\u03c3\u03b5\u03b9\u03c2 30%+.", en: "Free CRO insights that boost sales by 30%+" },
    cta: { el: "\u0398\u03ad\u03bb\u03c9 \u03c4\u03b1 insights", en: "I want the insights" },
  },
  haveSomething: {
    headline: { el: "\u0391\u03c0\u03bf\u03ba\u03bb\u03b5\u03b9\u03c3\u03c4\u03b9\u03ba\u03cc", en: "Exclusive" },
    subtitle: { el: "UX & CRO tips \u03c0\u03bf\u03c5 \u03b4\u03b5\u03bd \u03b8\u03b1 \u03b2\u03c1\u03b5\u03b9\u03c2 \u03b1\u03bb\u03bb\u03bf\u03cd.", en: "UX & CRO tips you won't find anywhere else." },
    cta: { el: "\u039d\u03b1\u03b9, \u03c3\u03c4\u03b5\u03af\u03bb\u03c4\u03b1 \u03bc\u03bf\u03c5", en: "Yes, send them" },
  },
  whereGoing: {
    headline: { el: "\u03a0\u03bf\u03c5 \u03c0\u03b1\u03c2;", en: "Where you going?" },
    subtitle: { el: "Weekly conversion tips \u03b1\u03c0\u03cc \u03c4\u03bf\u03c5\u03c2 \u03ba\u03bf\u03c1\u03c5\u03c6\u03b1\u03af\u03bf\u03c5\u03c2 marketers.", en: "Weekly conversion tips top marketers use." },
    cta: { el: "\u03a0\u03b5\u03c2 \u03bc\u03bf\u03c5 \u03c0\u03b5\u03c1\u03b9\u03c3\u03c3\u03cc\u03c4\u03b5\u03c1\u03b1", en: "Tell me more" },
  },
  exclusive: {
    headline: { el: "VIP Access", en: "VIP Access" },
    subtitle: { el: "Early access \u03c3\u03b5 AI tools \u03ba\u03b1\u03b9 proven strategies.", en: "Early access to AI tools and proven strategies." },
    cta: { el: "\u0398\u03ad\u03bb\u03c9 VIP access", en: "I want VIP access" },
  },
};

type CategoryKey = keyof typeof CATEGORY_COPY;

const getRandomCategory = (): CategoryKey => {
  const keys = Object.keys(CATEGORY_COPY) as CategoryKey[];
  return keys[Math.floor(Math.random() * keys.length)];
};

export function NewsletterPopup({ onClose, onSuccess }: NewsletterPopupProps) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [category] = useState<CategoryKey>(getRandomCategory);
  const { language } = useLanguage();

  const copy = CATEGORY_COPY[category];

  const t = {
    el: {
      badge: "Exclusive Access",
      headline: copy.headline.el,
      subtitle: copy.subtitle.el,
      valueProps: [
        "\u0395\u03b2\u03b4\u03bf\u03bc\u03b1\u03b4\u03b9\u03b1\u03af\u03b1 actionable CRO tips",
        "\u0391\u03c0\u03bf\u03ba\u03bb\u03b5\u03b9\u03c3\u03c4\u03b9\u03ba\u03ac case studies",
        "Early access \u03c3\u03b5 \u03bd\u03ad\u03b1 AI \u03b5\u03c1\u03b3\u03b1\u03bb\u03b5\u03af\u03b1"
      ],
      namePlaceholder: "\u03a4\u03bf \u03cc\u03bd\u03bf\u03bc\u03ac \u03c3\u03bf\u03c5...",
      emailPlaceholder: "\u03a4\u03bf email \u03c3\u03bf\u03c5...",
      cta: copy.cta.el,
      successTitle: "\u03a4\u03ad\u03bb\u03b5\u03b9\u03b1 \u03b5\u03c0\u03b9\u03bb\u03bf\u03b3\u03ae!",
      successMessage: "\u0395\u03c4\u03bf\u03b9\u03bc\u03ac\u03c3\u03bf\u03c5 \u03b3\u03b9\u03b1 UX & CRO insights \u03c3\u03c4\u03bf inbox \u03c3\u03bf\u03c5.",
      noThanks: "\u038c\u03c7\u03b9 \u03b5\u03c5\u03c7\u03b1\u03c1\u03b9\u03c3\u03c4\u03ce, \u03c0\u03c1\u03bf\u03c4\u03b9\u03bc\u03ce \u03bd\u03b1 \u03c7\u03ac\u03bd\u03c9 conversions",
      social: "Εβδομαδιαία, χωρίς spam, διαγραφή όποτε θέλεις",
    },
    en: {
      badge: "Exclusive Access",
      headline: copy.headline.en,
      subtitle: copy.subtitle.en,
      valueProps: [
        "Weekly actionable CRO tips",
        "Exclusive case studies with real results",
        "Early access to new AI tools"
      ],
      namePlaceholder: "Your name...",
      emailPlaceholder: "Your email...",
      cta: copy.cta.en,
      successTitle: "Great choice!",
      successMessage: "Get ready for game-changing UX & CRO insights in your inbox.",
      noThanks: "No thanks, I prefer losing conversions",
      social: "Weekly, no spam, unsubscribe any time",
    },
  };

  const text = t[language] || t.en;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);

    try {
      const { data, error } = await subscribeNewsletter({
        email,
        firstName,
        source: "exit_intent_popup",
      });

      if (error) throw error;

      if (data.already_subscribed) {
        toast.info(data.message);
      } else {
        toast.success(data.message);
      }
      setSuccess(true);
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || "Failed to subscribe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative p-6 md:p-8">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
      >
        <X className="h-4 w-4" />
      </button>

      {!success ? (
        <div className="flex flex-col items-center text-center">
          <EyeCaptainLogo
            alt="EyeCaptain"
            className="h-8 w-auto mb-4 opacity-80"
          />

          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium mb-3">
            <Zap className="h-3 w-3" />
            {text.badge}
          </span>

          <h2 className="text-xl font-bold mb-2">
            {text.headline}
          </h2>

          <p className="text-sm text-muted-foreground mb-5 leading-relaxed max-w-xs mx-auto">
            {text.subtitle}
          </p>

          <div className="space-y-2 mb-5 w-full max-w-xs mx-auto text-left">
            {text.valueProps.map((prop, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span>{prop}</span>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="w-full max-w-xs mx-auto space-y-2.5">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={text.namePlaceholder}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="pl-9 h-10 text-sm bg-muted/30 border-muted-foreground/20 focus:border-primary rounded-lg"
                disabled={loading}
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="email"
                placeholder={text.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-9 h-10 text-sm bg-muted/30 border-muted-foreground/20 focus:border-primary rounded-lg"
                required
                disabled={loading}
              />
            </div>
            <Button
              type="submit"
              size="lg"
              className="w-full h-11 text-sm font-semibold rounded-lg"
              disabled={loading}
            >
              {loading ? (
                <span className="animate-spin">
                  <Zap className="h-4 w-4" />
                </span>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  {text.cta}
                </>
              )}
            </Button>
          </form>

          <div className="mt-4 flex flex-col items-center gap-2">
            <span className="text-xs text-muted-foreground/60 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              {text.social}
            </span>
            <button
              onClick={onClose}
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors italic"
            >
              {text.noThanks}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center py-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-[hsl(var(--ec-mint))]" />
          </div>
          <h3 className="text-xl font-bold mb-2">{text.successTitle}</h3>
          <p className="text-sm text-muted-foreground max-w-xs">{text.successMessage}</p>
          <EyeCaptainLogo
            alt="EyeCaptain"
            className="h-7 w-auto opacity-40 mt-5"
          />
        </div>
      )}
    </div>
  );
}
